import { ensureMetaPortsInBottom } from '@/adapter/applicationLoader';
import { useApplication } from '@/hooks/useApplication';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { Connection } from '@/types/connection';
import { PortOption, PortType } from '@/types/metadata/metadata';
import { removeConnection, updateConnection } from '@/util/application';
import { FC } from 'react';
import CustomList from './CustomList';
import EnumList from './EnumList';
import FixedList from './FixedList';
import QueueList from './QueueList';

type ConnectionsRendererProps = {
  id: number;
  portOption: PortOption;
  type: PortType;
};

const ConnectionsRenderer: FC<ConnectionsRendererProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();

  const updateAddConnection = (newConnection: Connection) => {
    setApplication(
      !newConnection.action
        ? {
            ...application,
            blocks: {
              ...application.blocks,
              connections: {
                ...application.blocks.connections,
                [props.id]: [newConnection, ...application.blocks.connections[props.id]]
              }
            }
          }
        : updateConnection(props.id.toString(), newConnection, application)
    );
    setUpdateNodes([props.id.toString()]);
  };

  const replaceConnection = (newConnection: Connection, moveMetaPorts?: boolean) => {
    const newConnections = application.blocks.connections[props.id].map((connection) => {
      if (connection.id === newConnection.id) {
        return newConnection;
      } else {
        return connection;
      }
    });

    setApplication(
      updateConnection(
        props.id.toString(),
        moveMetaPorts ? ensureMetaPortsInBottom(newConnections, props.portOption?.ports || []) : newConnections,
        application
      )
    );
    setUpdateNodes([props.id.toString()]);
  };

  const changeArrayIndex = (data: Connection[]) => {
    setApplication(updateConnection(props.id.toString(), data, application));
    setUpdateNodes([props.id.toString()]);
  };

  const deleteConnection = (id: number) => {
    setApplication(removeConnection(props.id.toString(), id, application));
    setUpdateNodes([props.id.toString()]);
  };

  switch (props.type) {
    case 'fixed':
      return (
        <FixedList
          id={props.id}
          connections={application.blocks.connections[props.id]}
          onArrowClick={changeArrayIndex}
          updateAddConnection={updateAddConnection}
        />
      );

    case 'custom':
      return (
        <CustomList
          id={props.id}
          connections={application.blocks.connections[props.id]}
          portOption={props.portOption}
          onArrowClick={changeArrayIndex}
          updateAddConnection={updateAddConnection}
          replaceConnection={replaceConnection}
          deleteConnection={deleteConnection}
        />
      );

    case 'enum':
      return (
        <EnumList
          id={props.id}
          connections={application.blocks.connections[props.id]}
          portOption={props.portOption}
          onArrowClick={changeArrayIndex}
          updateAddConnection={updateAddConnection}
          replaceConnection={replaceConnection}
          deleteConnection={deleteConnection}
        />
      );

    case 'queue':
      return (
        <QueueList
          id={props.id}
          connections={application.blocks.connections[props.id]}
          portOption={props.portOption}
          onArrowClick={changeArrayIndex}
          updateAddConnection={updateAddConnection}
        />
      );

    default:
      return <></>;
  }
};

export default ConnectionsRenderer;
